import { getArtifactTypeName } from './types';
import { loadGroundOverlay, loadGroundOverlayFromUrl } from './groundOverlay';
import { loadVectorData, loadVectorDataFromUrl } from './vectorData';
import { loadScreenOverlay, loadScreenOverlayFromUrl } from './screenOverlay';
import { loadPlacemark } from './placemark';
import { loadInfoPanel } from './infoPanel';
import { demoProjectsService } from '../../../services/demoProjectsService';
import { projectService } from '../../../services/projectService';
const MAX_RETRIES = 2;
export const createLoadArtifact = (deps, options) => {
    const { artifactActions, artifactState, addStatus } = deps;
    const { viewerRef, isDemoProject } = options;
    const loadArtifact = (artifactId, metadata, onArtifactDataReceived) => {
        if (!metadata || typeof metadata.getName !== 'function') {
            console.error(`[loadArtifact] Invalid metadata for artifact ${artifactId}:`, metadata);
            addStatus(`Cannot load artifact - invalid metadata`);
            artifactActions.setLoading(artifactId, false);
            return;
        }
        if (artifactState.loaded.has(artifactId)) {
            addStatus(`Artifact ${metadata.getName()} is already loaded, skipping duplicate load`);
            return;
        }
        if (artifactState.loading.get(artifactId)) {
            addStatus(`Artifact ${metadata.getName()} is already loading, skipping duplicate request`);
            return;
        }
        artifactActions.setLoading(artifactId, true);
        const currentViewer = viewerRef.current;
        if (!currentViewer) {
            addStatus(`Viewer not ready for ${metadata.getName()}, retrying...`);
            setTimeout(() => loadArtifact(artifactId, metadata, onArtifactDataReceived), 100);
            return;
        }
        const currentRetryCount = artifactState.retryCount.get(artifactId) || 0;
        if (currentRetryCount >= MAX_RETRIES) {
            addStatus(`Maximum retry attempts (${MAX_RETRIES}) exceeded for artifact: ${metadata.getName()}`);
            artifactActions.setLoading(artifactId, false);
            return;
        }
        const artifactType = metadata.getType();
        const typeName = getArtifactTypeName(artifactType);
        artifactActions.incrementRetryCount(artifactId);
        addStatus(`Loading artifact: ${metadata.getName()} (${typeName})`);
        const loaderDeps = {
            viewer: currentViewer,
            artifactActions,
            artifactState,
            addStatus
        };
        const presignedUrl = metadata.getPresignedUrl ? metadata.getPresignedUrl() : '';
        const tryLoadViaUrl = (url, finalMetadata) => {
            addStatus(`Loading via presigned URL: ${finalMetadata.getName()}`);
            if (artifactType === 1) {
                try {
                    loadGroundOverlayFromUrl(artifactId, url, finalMetadata, loaderDeps);
                }
                catch (error) {
                    artifactActions.setLoading(artifactId, false);
                    addStatus(`Error loading artifact: ${error}`);
                }
                return;
            }
            if (artifactType === 2) {
                try {
                    loadVectorDataFromUrl(artifactId, url, finalMetadata, loaderDeps);
                }
                catch (error) {
                    artifactActions.setLoading(artifactId, false);
                    addStatus(`Error loading artifact: ${error}`);
                }
                return;
            }
            if (artifactType === 3) {
                try {
                    loadScreenOverlayFromUrl(artifactId, url, finalMetadata, loaderDeps);
                }
                catch (error) {
                    artifactActions.setLoading(artifactId, false);
                    addStatus(`Error loading artifact: ${error}`);
                }
                return;
            }
            fetch(url)
                .then(async (resp) => {
                if (!resp.ok)
                    throw new Error(`HTTP ${resp.status}`);
                const arr = await resp.arrayBuffer();
                const bytes = new Uint8Array(arr);
                try {
                    if (artifactType === 4) {
                        loadPlacemark(artifactId, bytes, finalMetadata, loaderDeps);
                    }
                    else if (artifactType === 5) {
                        loadInfoPanel(artifactId, bytes, finalMetadata, loaderDeps);
                    }
                    else {
                        addStatus(`Unknown artifact type: ${artifactType}`);
                    }
                }
                catch (error) {
                    artifactActions.setLoading(artifactId, false);
                    addStatus(`Error loading artifact: ${error}`);
                }
            })
                .catch(async () => {
                try {
                    let response;
                    if (isDemoProject) {
                        response = await demoProjectsService.getArtifact(artifactId);
                    }
                    else {
                        response = await projectService.getUserProjectArtifact(artifactId);
                    }
                    if (response) {
                        const respMeta = response.getMetadata();
                        const fallbackUrl = (respMeta === null || respMeta === void 0 ? void 0 : respMeta.getPresignedUrl) ? respMeta.getPresignedUrl() : '';
                        if (fallbackUrl) {
                            tryLoadViaUrl(fallbackUrl, respMeta || finalMetadata);
                        }
                        else {
                            const blobData = response.getBlobData();
                            if (blobData && blobData.length > 0) {
                                try {
                                    if (artifactType === 1) {
                                        loadGroundOverlay(artifactId, blobData, respMeta || finalMetadata, loaderDeps);
                                    }
                                    else if (artifactType === 2) {
                                        loadVectorData(artifactId, blobData, respMeta || finalMetadata, loaderDeps);
                                    }
                                    else if (artifactType === 3) {
                                        loadScreenOverlay(artifactId, blobData, respMeta || finalMetadata, loaderDeps);
                                    }
                                    else if (artifactType === 4) {
                                        loadPlacemark(artifactId, blobData, respMeta || finalMetadata, loaderDeps);
                                    }
                                    else if (artifactType === 5) {
                                        loadInfoPanel(artifactId, blobData, respMeta || finalMetadata, loaderDeps);
                                    }
                                }
                                catch (error) {
                                    artifactActions.setLoading(artifactId, false);
                                    addStatus(`Error loading artifact: ${error}`);
                                }
                            }
                            else {
                                artifactActions.setLoading(artifactId, false);
                                addStatus(`No presigned URL or blob for artifact: ${metadata.getName()}`);
                            }
                        }
                    }
                    else {
                        artifactActions.setLoading(artifactId, false);
                        addStatus(`No response received from artifact service`);
                    }
                }
                catch (error) {
                    artifactActions.setLoading(artifactId, false);
                    addStatus(`Error loading artifact: ${error.message || error}`);
                }
            });
        };
        if (presignedUrl) {
            tryLoadViaUrl(presignedUrl, metadata);
            return;
        }
        const handleArtifactResponse = (err, response) => {
            if (err) {
                artifactActions.setLoading(artifactId, false);
                addStatus(`Error loading artifact: ${err.code} ${err.message}`);
            }
            else if (response) {
                const blobData = response.getBlobData();
                const responseMetadata = response.getMetadata();
                if (blobData && blobData.length > 0) {
                    addStatus(`Received artifact data for: ${metadata.getName()} (${(blobData.length / 1024).toFixed(1)} KB)`);
                    const finalMetadata = responseMetadata || metadata;
                    if (onArtifactDataReceived) {
                        onArtifactDataReceived(artifactId, blobData, finalMetadata);
                    }
                    else {
                        try {
                            if (artifactType === 1) {
                                loadGroundOverlay(artifactId, blobData, finalMetadata, loaderDeps);
                            }
                            else if (artifactType === 2) {
                                loadVectorData(artifactId, blobData, finalMetadata, loaderDeps);
                            }
                            else if (artifactType === 3) {
                                loadScreenOverlay(artifactId, blobData, finalMetadata, loaderDeps);
                            }
                            else if (artifactType === 4) {
                                loadPlacemark(artifactId, blobData, finalMetadata, loaderDeps);
                            }
                            else if (artifactType === 5) {
                                loadInfoPanel(artifactId, blobData, finalMetadata, loaderDeps);
                            }
                            else {
                                addStatus(`Unknown artifact type: ${artifactType}`);
                            }
                        }
                        catch (error) {
                            addStatus(`Error loading artifact: ${error}`);
                        }
                    }
                }
                else {
                    artifactActions.setLoading(artifactId, false);
                    addStatus(`No blob data received for artifact: ${metadata.getName()}`);
                }
            }
        };
        if (isDemoProject) {
            demoProjectsService.getArtifact(artifactId)
                .then(response => handleArtifactResponse(null, response))
                .catch(err => handleArtifactResponse(err, null));
        }
        else {
            projectService.getUserProjectArtifact(artifactId)
                .then(response => handleArtifactResponse(null, response))
                .catch(err => handleArtifactResponse(err, null));
        }
    };
    return loadArtifact;
};
